if (!customElements.get('community-carousel')) {
  let instagramEmbedScriptPromise;

  const loadInstagramEmbeds = () => {
    if (window.instgrm && window.instgrm.Embeds) {
      window.instgrm.Embeds.process();
      return;
    }

    if (!instagramEmbedScriptPromise) {
      instagramEmbedScriptPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://www.instagram.com/embed.js';
        script.async = true;
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener(
          'error',
          () => {
            script.remove();
            instagramEmbedScriptPromise = null;
            resolve();
          },
          { once: true }
        );
        document.head.appendChild(script);
      });
    }

    instagramEmbedScriptPromise.then(() => {
      if (window.instgrm && window.instgrm.Embeds) {
        window.instgrm.Embeds.process();
      }
    });
  };

  customElements.define(
    'community-carousel',
    class CommunityCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-community-track]');
        this.list = this.querySelector('[data-community-list]');

        if (!this.track || !this.list) return;

        this.previousButton = this.querySelector('[data-community-direction="previous"]');
        this.nextButton = this.querySelector('[data-community-direction="next"]');
        this.onClick = this.onClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.updateButtons = this.updateButtons.bind(this);

        this.addEventListener('click', this.onClick);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.updateButtons);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.updateButtons);
        }

        this.updateButtons();

        if (this.querySelector('.instagram-media')) {
          this.observeInstagramEmbeds();
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);

        if (this.instagramObserver) {
          this.instagramObserver.disconnect();
          this.instagramObserver = null;
        }

        if (this.track) {
          this.track.removeEventListener('scroll', this.onScroll);
        }

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        } else {
          window.removeEventListener('resize', this.updateButtons);
        }
      }

      observeInstagramEmbeds() {
        if (!('IntersectionObserver' in window)) {
          loadInstagramEmbeds();
          return;
        }

        const observer = new IntersectionObserver(
          (entries) => {
            if (!this.isConnected || !entries.some((entry) => entry.isIntersecting)) return;

            observer.disconnect();

            if (this.instagramObserver === observer) {
              this.instagramObserver = null;
            }

            loadInstagramEmbeds();
          },
          { rootMargin: '300px 0px' }
        );

        this.instagramObserver = observer;
        observer.observe(this);
      }

      onClick(event) {
        const button = event.target.closest('[data-community-direction]');

        if (!button || button.disabled) return;

        const firstCard = this.list.querySelector('[data-community-card]');
        const gap = parseFloat(window.getComputedStyle(this.list).columnGap) || 0;
        const distance = firstCard ? firstCard.getBoundingClientRect().width + gap : this.track.clientWidth;
        const direction = button.dataset.communityDirection === 'previous' ? -1 : 1;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.track.scrollBy({ left: direction * distance, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      onScroll() {
        window.requestAnimationFrame(this.updateButtons);
      }

      updateButtons() {
        if (!this.previousButton || !this.nextButton) return;

        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const canScroll = maxScroll > 1;

        this.previousButton.hidden = !canScroll;
        this.nextButton.hidden = !canScroll;
        this.previousButton.disabled = !canScroll || this.track.scrollLeft <= 1;
        this.nextButton.disabled = !canScroll || this.track.scrollLeft >= maxScroll - 1;
      }
    }
  );
}
